// src/pages/DashboardPage.js

import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import PageContainer from '../components/common/PageContainer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import StatCard from '../components/common/StatCard';
import ReportCard from '../components/common/ReportCard';
import useApi from '../hooks/useApi';
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar
} from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { HiOutlineCash, HiOutlineUser } from 'react-icons/hi';
import { HiOutlineCurrencyDollar, HiOutlineKey, HiOutlineUsers } from 'react-icons/hi2';

// small presentational helper for KPI tiles when needed
const KpiTile = ({ label, value }) => (
  <div className="flex flex-col items-start gap-1">
    <div className="text-xs text-gray-400">{label}</div>
    <div className="text-lg font-semibold text-gray-800">{value}</div>
  </div>
);

// Custom Tooltip for Recharts — slightly restyled
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-3 bg-white border border-gray-200 rounded-md shadow-md text-sm">
        <div className="font-medium text-gray-700 mb-1">{label}</div>
        {payload.map((p, index) => (
          <div key={index} className="flex justify-between text-gray-700">
            <div>{p.name}</div>
            <div className="font-semibold">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value} {p.unit || ''}</div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const DashboardPage = () => {
  const api = useApi();
  const [stats, setStats] = useState({
    users: 0,
    customers: 0,
    transactions: 0,
    roles: 0,
    permissions: 0
  });
  const [salesSummary, setSalesSummary] = useState(null);
  const [stockAging, setStockAging] = useState(null);
  const [salesTrendData, setSalesTrendData] = useState([]);
  const [topProductsData, setTopProductsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // counts (defensive - some endpoints may return wrappers)
      const u = await api.get('users/count'); const usersRes = u?.data ?? u;
      const c = await api.get('customers/count'); const customersRes = c?.data ?? c;
      const t = await api.get('transactions/count'); const transactionsRes = t?.data ?? t;
      const r = await api.get('roles/count'); const rolesRes = r?.data ?? r;
      const p = await api.get('permissions/count'); const permissionsRes = p?.data ?? p;

      setStats({
        users: usersRes?.count ?? 0,
        customers: customersRes?.count ?? 0,
        transactions: transactionsRes?.count ?? 0,
        roles: rolesRes?.count ?? 0,
        permissions: permissionsRes?.count ?? 0,
      });

      // date range for summary/trends
      const today = new Date();
      const sevenDaysAgo = subDays(today, 6);

      // sales trend
      try {
        const st = await api.get('reports/sales-trend', {
          queryParams: {
            startDate: format(sevenDaysAgo, 'yyyy-MM-dd'),
            endDate: format(today, 'yyyy-MM-dd'),
            interval: 'day'
          }
        });
        const stData = st?.data?.data ?? st?.data ?? st ?? [];
        setSalesTrendData(Array.isArray(stData) ? stData.map(item => ({ ...item, date: format(parseISO(item.date), 'MMM dd') })) : []);
      } catch (err) {
        setSalesTrendData([]);
        console.warn('sales trend load failed', err);
      }

      // top products
      try {
        const tp = await api.get('reports/top-products', { queryParams: { limit: 5 } });
        const tpData = tp?.data?.data ?? tp?.data ?? tp ?? [];
        setTopProductsData(Array.isArray(tpData) ? tpData : []);
      } catch (err) {
        setTopProductsData([]);
        console.warn('top products load failed', err);
      }

      // sales summary
      try {
        const summaryResp = await api.get('reports-analysis/sales-summary', {
          queryParams: {
            startDate: format(sevenDaysAgo, 'yyyy-MM-dd'),
            endDate: format(today, 'yyyy-MM-dd')
          }
        });
        const summary = summaryResp?.data?.data ?? summaryResp?.data ?? summaryResp ?? null;
        setSalesSummary(summary && Object.keys(summary).length ? summary : null);
      } catch (err) {
        console.warn('sales summary load failed', err);
        setSalesSummary(null);
      }

      // stock aging
      try {
        const agingResp = await api.get('reports-analysis/stock-aging');
        const aging = agingResp?.data?.data ?? agingResp?.data ?? agingResp ?? null;
        setStockAging(aging && Object.keys(aging).length ? aging : null);
      } catch (err) {
        console.warn('stock aging load failed', err);
        setStockAging(null);
      }
    } catch (err) {
      setError(err?.message || 'Failed to fetch dashboard data.');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  return (
    <Layout>
      <PageContainer title="Dashboard">
        {loading ? (
          <div className="py-16 flex justify-center"><LoadingSpinner /></div>
        ) : error ? (
          <div className="py-8"><ErrorMessage message={error} /></div>
        ) : (
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Top Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-white rounded-lg shadow-sm p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-indigo-600 flex items-center justify-center text-white">
                  <HiOutlineUser/>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Total Users</div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.users}</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                  <HiOutlineUsers/>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Customers</div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.customers}</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-purple-600 flex items-center justify-center text-white">
                  <HiOutlineCash/>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Transactions</div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.transactions}</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-amber-500 flex items-center justify-center text-white">
                  <HiOutlineKey/>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Permissions</div>
                  <div className="text-2xl font-semibold text-gray-900">{stats.permissions}</div>
                </div>
              </div>
            </div>

            {/* Sales Summary Cards */}
            {salesSummary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard title="Total Revenue" value={`$${Number(salesSummary.totalRevenue ?? 0).toLocaleString(undefined, {minimumFractionDigits:2})}`} icon="💰" color="bg-indigo-600" />
                <StatCard title="Total COGS" value={`$${Number(salesSummary.totalCOGS ?? 0).toLocaleString(undefined, {minimumFractionDigits:2})}`} icon="📦" color="bg-gray-700" />
                <StatCard title="Gross Profit" value={`$${Number(salesSummary.grossProfit ?? 0).toLocaleString(undefined, {minimumFractionDigits:2})}`} icon="📈" color="bg-emerald-600" />
                <StatCard title="Transactions" value={salesSummary.transactionCount ?? '—'} icon="🧾" color="bg-purple-600" />
              </div>
            )}

            {/* KPIs row */}
            {salesSummary && (
              <div className="bg-white rounded-lg shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex gap-6">
                  <KpiTile label="Gross Margin" value={`${((Number(salesSummary.totalRevenue ?? 0) - Number(salesSummary.totalCOGS ?? 0)) / (Number(salesSummary.totalRevenue ?? 1)) * 100).toFixed(2)}%`} />
                  <KpiTile label="Avg Order Value" value={`$${(Number(salesSummary.totalRevenue ?? 0) / (Number(salesSummary.transactionCount ?? 1))).toFixed(2)}`} />
                </div>
                <div className="text-xs text-gray-500">Source: reports-analysis/sales-summary • Period: last 7 days</div>
              </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ReportCard title="Sales Trend (Last 7 Days)" className="h-80">
                {salesTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={salesTrendData} margin={{ top: 12, right: 20, left: -10, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#6b7280' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="totalSales" stroke="#6366f1" strokeWidth={3} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="p-6 text-sm text-gray-500">No sales data available for the selected period.</div>
                )}
              </ReportCard>

              <ReportCard title="Top 5 Products (Quantity)" className="h-80">
                {topProductsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={topProductsData} layout="vertical" margin={{ top: 12, right: 20, left: 10, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fill: '#6b7280' }} />
                      <YAxis dataKey="productName" type="category" width={150} tick={{ fill: '#374151' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="totalQuantity" fill="#10b981" radius={[4,4,4,4]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="p-6 text-sm text-gray-500">No top product data available.</div>
                )}
              </ReportCard>
            </div>

            {/* Stock Aging: chart in its card, breakdown cards rendered below as a separate row to avoid overlap */}
            <div className="mt-4">
              <ReportCard title="Stock Aging">
                {stockAging ? (
                  <div className="w-full h-56 overflow-hidden rounded-md bg-white p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={Object.entries(stockAging).map(([bucket, v]) => ({ bucket, totalQuantity: v?.totalQuantity ?? 0 }))}
                        margin={{ top: 6, right: 12, left: 0, bottom: 6 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="bucket" tick={{ fill: '#374151' }} />
                        <YAxis tick={{ fill: '#6b7280' }} />
                        <Tooltip />
                        <Bar dataKey="totalQuantity" fill="#f59e0b" radius={[6,6,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="p-6 text-sm text-gray-500">No stock aging data available.</div>
                )}
              </ReportCard>

              {/* Separate row of breakdown cards (placed outside ReportCard to prevent visual overlap) */}
              {stockAging && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {Object.entries(stockAging).map(([bucket, v]) => (
                    <div key={bucket} className="bg-white rounded-lg shadow-sm p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">{bucket}</div>
                        <div className="text-sm text-gray-400">{v?.products?.length ?? 0} SKUs</div>
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-gray-900">{v?.totalQuantity ?? 0}</div>
                      {Array.isArray(v?.products) && v.products.length > 0 && (
                        <div className="mt-3 text-sm text-gray-600">
                          One of Product: <span className="font-medium text-gray-800">{v.products[0].productId || v.products[0].lotNumber || '—'}</span>
                          <span className="ml-2 text-gray-500">• {v.products[0].quantity ?? 0} units</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </PageContainer>
    </Layout>
  );
};

export default DashboardPage;
